import { z } from 'zod/v3';

// ============================================================================
// 공통 Enum 스키마
// ============================================================================

/** 성별 */
export const genderSchema = z.enum(['NONE', 'MALE', 'FEMALE']);
export type Gender = z.infer<typeof genderSchema>;

/** 국적 */
export const nationalitySchema = z.enum(['NONE', 'KOREAN', 'FOREIGNER']);
export type Nationality = z.infer<typeof nationalitySchema>;

/** 로그인 타입 */
export const loginTypeSchema = z.enum(['NORMAL', 'NONE_LOGIN', 'PRE_BUSINESS']);
export type LoginType = z.infer<typeof loginTypeSchema>;

/** 플러스 멤버십 타입 */
export const plusMembershipTypeSchema = z.enum([
  'NOT_REGISTERED',
  'BASIC',
  'STANDARD',
  'STARTER',
]);
export type PlusMembershipType = z.infer<typeof plusMembershipTypeSchema>;

// ============================================================================
// 서브 스키마
// ============================================================================

/** 사업장 정보 */
export const buyerBusinessSchema = z.object({
  /** 사업장 ID */
  id: z.number().optional(),
  /** 사업장 Public ID */
  publicId: z.number().optional(),
  /** 상호명 */
  name: z.string().optional(),
  /** 사업자 등록번호 */
  registrationNumber: z.string().optional(),
});
export type BuyerBusiness = z.infer<typeof buyerBusinessSchema>;

/** 배송 주소 */
export const shippingAddressSchema = z.object({
  /** 주소 ID */
  id: z.number().optional(),
  /** 수령인 이름 */
  recipientName: z.string().optional(),
  /** 연락처 */
  phoneNumber: z.string().optional(),
  /** 주소 */
  address: z.string().optional(),
  /** 상세 주소 */
  detailAddress: z.string().optional(),
  /** 우편번호 */
  zipCode: z.string().optional(),
  /** 기본 주소 여부 */
  isDefault: z.boolean().optional(),
});
export type ShippingAddress = z.infer<typeof shippingAddressSchema>;

/** 구독 정보 */
export const buyerSubscriptionSchema = z.object({
  /** 플멤 구독 가능 여부 */
  isAvailableToSubscribe: z.boolean(),
  /** 마켓 혜택 사용 여부 */
  isMarketBenefitEnabled: z.boolean().optional(),
  /** 플멤 사용 여부 */
  isPlusMembership: z.boolean().optional(),
  /** 보너스팩 가입 여부 */
  isBonusPack: z.boolean().optional(),
});
export type BuyerSubscription = z.infer<typeof buyerSubscriptionSchema>;

/** 플랜 상세 */
export const subscriptionPlanDetailSchema = z.object({
  /** 플랜 이름 */
  planName: z.string().optional(),
  /** 월 요금 */
  monthlyPrice: z.number().optional(),
  /** 다음 결제일 */
  nextPaymentDate: z.string().optional(),
});
export type SubscriptionPlanDetail = z.infer<
  typeof subscriptionPlanDetailSchema
>;

// ============================================================================
// Model 스키마
// ============================================================================

/**
 * 구매자 정보 스키마
 */
export const buyerSchema = z.object({
  /** 구매자 ID */
  id: z.number().optional(),
  /** 캐시노트 사용자 ID */
  userId: z.number().optional(),
  /** 캐시노트 UUID */
  uuid: z.string().optional(),
  /** 구매자명 */
  name: z.string().optional(),
  /** 전화번호 */
  phoneNumber: z.string().optional(),
  /** 이메일 주소 */
  emailAddress: z.string().optional(),
  /** 생년월일 */
  birthday: z.string().optional(),
  /** 성별 */
  gender: genderSchema.optional(),
  /** 국적 */
  nationality: nationalitySchema.optional(),
  /** 로그인 타입 */
  loginType: loginTypeSchema,
  /** 플러스 멤버십 타입 */
  plusMembershipType: plusMembershipTypeSchema.optional(),
  /** 사업장 정보 */
  business: buyerBusinessSchema.optional(),
  /** 사업장 목록 */
  businesses: z.array(buyerBusinessSchema),
  /** 주소 목록 */
  addresses: z.array(shippingAddressSchema),
  /** 구독 정보 */
  subscription: buyerSubscriptionSchema,
  /** 상호명 */
  businessName: z.string().optional(),
  /** 사업장 Public ID */
  businessPublicId: z.number().optional(),
  /** 기본 지역 코드 */
  defaultRegionCode: z.string(),
  /** 기본 배송지 등록 여부 */
  hasDefaultAddressRegion: z.boolean(),
  /** 포인트 항상 사용 여부 */
  alwaysUsePoint: z.boolean(),
  /** 구매자 생성 여부 */
  isCreated: z.boolean().optional(),
  /** 신규 회원 여부 */
  isNewMember: z.boolean().optional(),
  /** 주문 여부 */
  isOrdered: z.boolean().optional(),
  /** 주문 수 */
  orderCount: z.number().optional(),
  /** 약관 동의 여부 */
  isTermAgreed: z.boolean().optional(),
  /** 캐시노트 플러스 가입 가능 여부 */
  cashnotePlusAvailable: z.boolean().optional(),
  /** 캐시노트 플러스 가입 여부 */
  cashnotePlusEnrolled: z.boolean().optional(),
  /** 100원딜 여부 */
  isFirstOrderPlusMembership100Deal: z.boolean(),
  /** 추적용 ID */
  trackingId: z.string().optional(),
  /** 생성일 */
  createdAt: z.string().optional(),
  /** 수정일 */
  updatedAt: z.string().optional(),
});
export type Buyer = z.infer<typeof buyerSchema>;

/**
 * 구매자 플러스 멤버십 정보 스키마
 */
export const buyerPlusMembershipSchema = z.object({
  /** 구매자 ID */
  id: z.number().optional(),
  /** 플멤 구독 가능 여부 */
  isAvailableToSubscribe: z.boolean(),
  /** 보너스팩 가입 여부 */
  isBonusPack: z.boolean(),
  /** 플멤 사용 여부 */
  isPlusMembership: z.boolean(),
  /** 무료 체험 가능 여부 */
  isTrialAvailable: z.boolean(),
  /** 가입된 플랜 상세 */
  planDetail: subscriptionPlanDetailSchema.optional(),
  /** 보너스팩 등록 가능 여부 */
  registerableMembershipOptionLuckyBonusPack: z.boolean().optional(),
  /** 총 쿠폰 할인 금액 */
  totalCouponDiscountPrice: z.number(),
});
export type BuyerPlusMembership = z.infer<typeof buyerPlusMembershipSchema>;

/** 할인 사용 내역 */
export const buyerDiscountUsageSchema = z.object({
  /** 할인 타입 */
  discountType: z.string().optional(),
  /** 사용 금액 */
  usedAmount: z.number().optional(),
  /** 남은 금액 */
  remainAmount: z.number().optional(),
});
export type BuyerDiscountUsage = z.infer<typeof buyerDiscountUsageSchema>;

/**
 * 구매자 마이페이지 정보 스키마
 */
export const buyerMyPageSchema = z.object({
  /** 구매자 ID */
  buyerId: z.number(),
  /** 사용자 ID */
  userId: z.number(),
  /** 할인 사용 내역 */
  discountUsages: z.array(buyerDiscountUsageSchema),
});
export type BuyerMyPage = z.infer<typeof buyerMyPageSchema>;
